import { useEffect, useState } from 'react';
import api from '../utils/api';
import { X, ClipboardCheck } from 'lucide-react';

// Shared drawer: full profile for one technician — stats, per-item breakdown
// (if provided), and their completed jobs with notes.
export default function TechProfileDrawer({ tech, branch, stats, onClose }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tech) return;
    setLoading(true);
    api.get('/jobs/completed', { params: { tech, branch, per_page: 200 } })
      .then(r => setJobs(r.data.jobs || []))
      .finally(() => setLoading(false));
  }, [tech, branch]);

  if (!tech) return null;

  const passed  = jobs.filter(j => j.result === 'Pass');
  const flagged = jobs.filter(j => j.result === 'Flag');
  const flaggedItems = stats?.items?.filter(i => i.flagged > 0) || [];

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="bg-[#3D2B1A] text-white px-6 py-4 flex items-start justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold">{tech}</h2>
            <p className="text-white/60 text-sm mt-0.5">{branch}</p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <span>{jobs.length} completed</span>
              <span className="text-green-400">{passed.length} Pass</span>
              <span className="text-red-400">{flagged.length} Flag</span>
              {(stats?.pass_rate ?? null) != null && (
                <span className="text-[#E8761A] font-semibold">{stats.pass_rate}% pass rate</span>
              )}
              {(stats?.avg_score ?? null) != null && (
                <span className="text-white/70">{stats.avg_score}% avg score</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white mt-1">
            <X size={20} />
          </button>
        </div>

        {stats?.items && (
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60 flex-shrink-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Checklist Item Breakdown</h3>
            <div className="grid grid-cols-2 gap-2">
              {stats.items.map(i => (
                <div key={i.field} className="bg-white border border-gray-100 rounded-lg px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-gray-600">{i.item}</span>
                  <span className="flex items-center gap-2 text-xs font-semibold">
                    <span className="text-red-500">{i.flagged} flagged</span>
                    <span className="text-gray-300">/</span>
                    <span className="text-green-600">{i.not_flagged} ok</span>
                  </span>
                </div>
              ))}
            </div>
            {flaggedItems.length === 0 && (
              <p className="text-xs text-green-600 mt-2">No flagged items — clean record on every checklist item.</p>
            )}
          </div>
        )}

        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <ClipboardCheck size={13} /> Completed Jobs &amp; Notes
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No completed jobs found</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {jobs.map(job => (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-[#E8761A] text-sm font-semibold">#{job.job_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          job.result === 'Pass' ? 'bg-green-100 text-green-700' :
                          job.result === 'Flag' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-500'}`}>
                          {job.result || '—'}
                        </span>
                        {job.score != null && (
                          <span className={`text-xs font-semibold ${job.score >= 0.9 ? 'text-green-600' : job.score >= 0.75 ? 'text-amber-600' : 'text-red-600'}`}>
                            {Math.round(job.score * 100)}%
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
                        {job.job_type && <span>{job.job_type}</span>}
                        {job.customer_name && <span>{job.customer_name}</span>}
                        {job.scheduled_date && <span>{new Date(job.scheduled_date).toLocaleDateString()}</span>}
                        {job.completed_at && (
                          <span className="text-gray-400">Completed {new Date(job.completed_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      {job.notes && job.notes !== '0' && (
                        <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg">
                          <div className="text-xs font-semibold text-amber-700 mb-0.5">Auditor Notes</div>
                          <div className="text-xs text-amber-800 leading-relaxed">{job.notes}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
